import LazyLoad from 'vanilla-lazyload'

declare global {
    interface Window {
        _AVENCY_BASE_actions: {
            referenceList: string;
        };
        LazyLoad: LazyLoad;
    }
}

class ReferenceList {
    private readonly preFilterServices: string[] = [];
    private readonly preFilterTechnologies: string[] = [];
    private readonly preFilterCustomers: string[] = [];
    private readonly preFilterBranches: string[] = [];
    private readonly preFilterServiceAreas: string[] = [];
    private filter: {services: string[], technologies: string[], branches: string[]};
    private referenceList: HTMLElement;
    private wrapper: HTMLElement;
    private container: HTMLElement;
    private loadMoreButton: HTMLElement;
    private wrapperId: string;
    private readonly identifier: string;
    private readonly showCards: number = 4;
    private readonly limit: number = 30;
    private readonly type: string = 'filter';
    private currentPage: number = 1;
    private controller: AbortController|null = null;

    constructor(referenceList: HTMLElement) {
        this.referenceList = referenceList;
        this.wrapper = referenceList.closest('.reference-list-wrapper');
        this.container = referenceList.closest('.reference-list-container');
        this.loadMoreButton = this.wrapper.querySelector('.reference-list__show-more');

        this.wrapperId = this.wrapper.getAttribute('id');
        this.identifier = referenceList.dataset.identifier;
        this.showCards = parseInt(referenceList.dataset.showcards);
        this.limit = parseInt(referenceList.dataset.limit);
        this.type = referenceList.dataset.type;

        this.preFilterServiceAreas = referenceList.dataset.serviceareas ? JSON.parse(referenceList.dataset.serviceareas) : {};
        this.preFilterServices = referenceList.dataset.services ? JSON.parse(referenceList.dataset.services) : {};
        this.preFilterTechnologies = referenceList.dataset.technologies ? JSON.parse(referenceList.dataset.technologies) : {};
        this.preFilterBranches = referenceList.dataset.branches ? JSON.parse(referenceList.dataset.branches) : {};
        this.preFilterCustomers = referenceList.dataset.customer ? JSON.parse(referenceList.dataset.customer) : {};

        this.filter = {
            services: [],
            technologies: [],
            branches: []
        }

        this.loadMoreButton.addEventListener('click', () => {
            this.currentPage++;
            this.getReferenceListData();
        });
        this.getReferenceListData();
        this.applyFilter();
        this.checkIfCategoryOrTagIsEmpty();
    }

    public checkIfCategoryOrTagIsEmpty = () => {
        const referenceFilterWrapper = this.container.querySelector('.reference-list-filter');

        if (referenceFilterWrapper) {
            const filterServices = referenceFilterWrapper.querySelector('.filter-services');
            if (filterServices.querySelectorAll('.dropdown-menu li:not(.d-none)').length === 0) {
                filterServices.querySelector('button').classList.add('disabled');
            } else {
                filterServices.querySelector('button').classList.remove('disabled');
            }

            const filterTechnologies = referenceFilterWrapper.querySelector('.filter-technologies');
            if (filterTechnologies.querySelectorAll('.dropdown-menu li:not(.d-none)').length === 0) {
                filterTechnologies.querySelector('button').classList.add('disabled');
            } else {
                filterTechnologies.querySelector('button').classList.remove('disabled');
            }

            const filterBranches = referenceFilterWrapper.querySelector('.filter-branches');
            if (filterBranches.querySelectorAll('.dropdown-menu li:not(.d-none)').length === 0) {
                filterBranches.querySelector('button').classList.add('disabled');
            } else {
                filterBranches.querySelector('button').classList.remove('disabled');
            }
        }
    }

    public getReferenceListData() {
        // Abort previous request
        if (this.controller) {
            this.controller.abort();
            this.controller = new AbortController();
        } else {
            this.controller = new AbortController();
        }

        fetch(window._AVENCY_BASE_actions.referenceList, {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            signal: this.controller.signal,
            body: JSON.stringify({
                showCards: this.showCards,
                limit: this.limit,
                type: this.type,
                currentPage: this.currentPage,
                identifier: this.identifier,
                filters: this.filter,
                preFilters: this.getPreFilters()
            })
        })
            .then(response => response.text())
            .then(data => {
                if (this.currentPage === 1) {
                    this.referenceList.innerHTML = data;
                } else {
                    this.referenceList.insertAdjacentHTML('beforeend', data);
                }

                const dataCountEl: HTMLElement = this.referenceList.querySelector('div[data-count]');
                const count = dataCountEl.dataset.count;
                const currentCount = this.currentPage * this.showCards;

                Array.from(this.referenceList.querySelectorAll('img')).forEach((picture: HTMLImageElement) => {
                    LazyLoad.load(picture);
                });

                // Workaround: sometimes columns without content are shown
                const brokenReferences = document.querySelectorAll('.reference-list .col-md-6:not(:has(:nth-child(1)))');
                if (brokenReferences.length > 0) {
                    brokenReferences.forEach((reference: HTMLElement) => {
                        reference.remove();
                    });
                }

                if (this.limit > 0 || currentCount >= count) {
                    this.loadMoreButton.classList.add('d-none');
                } else {
                    this.loadMoreButton.classList.remove('d-none');
                }
            })
            .catch(e => {
                if (e.name === 'AbortError') {
                    // do nothing
                }
            });
    }

    public filterReferences = () => {
        const filterServices = this.container.querySelectorAll('.filter-services li.active .dropdown-item');
        const filterTechnologies = this.container.querySelectorAll('.filter-technologies li.active .dropdown-item');
        const filterBranches = this.container.querySelectorAll('.filter-branches li.active .dropdown-item');

        // reset filter
        this.filter.services = [];
        this.filter.technologies = [];
        this.filter.branches = [];

        if (filterServices.length > 0) {
            filterServices.forEach((filterItem: HTMLElement) => {
                const identifier = filterItem.dataset.identifier;

                if (identifier) {
                    this.filter.services.push(identifier);
                }
            });
        }
        if (filterTechnologies.length > 0) {
            filterTechnologies.forEach((filterItem: HTMLElement) => {
                const identifier = filterItem.dataset.identifier;

                if (identifier) {
                    this.filter.technologies.push(identifier);
                }
            });
        }
        if (filterBranches.length > 0) {
            filterBranches.forEach((filterItem: HTMLElement) => {
                const identifier = filterItem.dataset.identifier;

                if (identifier) {
                    this.filter.branches.push(identifier);
                }
            });
        }
        this.currentPage = 1;
        this.getReferenceListData();
    }

    public applyFilter = () => {
        const filterServices = this.container.querySelectorAll('.filter-services li');
        const filterTechnologies = this.container.querySelectorAll('.filter-technologies li');
        const filterBranches = this.container.querySelectorAll('.filter-branches li');

        Array.from(filterServices).forEach((filterItem: HTMLElement) => {
            filterItem.addEventListener('click', () => {
                if (filterItem.classList.contains('active')) {
                    filterItem.classList.remove('active');
                } else {
                    filterItem.classList.add('active');
                }

                this.filterReferences();
            });
        });

        Array.from(filterTechnologies).forEach((filterItem: HTMLElement) => {
            filterItem.addEventListener('click', () => {
                if (filterItem.classList.contains('active')) {
                    filterItem.classList.remove('active');
                } else {
                    filterItem.classList.add('active');
                }

                this.filterReferences();
            });
        });

        Array.from(filterBranches).forEach((filterItem: HTMLElement) => {
            filterItem.addEventListener('click', () => {
                if (filterItem.classList.contains('active')) {
                    filterItem.classList.remove('active');
                } else {
                    filterItem.classList.add('active');
                }

                this.filterReferences();
            });
        });

    }

    public getPreFilters = () => {
        return {
            serviceAreas: this.preFilterServiceAreas,
            services: this.preFilterServices,
            technologies: this.preFilterTechnologies,
            branches: this.preFilterBranches,
            customer: this.preFilterCustomers
        }
    }
}

export default ReferenceList;
